import React from 'react'
import { css, cx } from '@emotion/css'
import { theme as deprecatedTheme } from '../../styled'
import {
  theme,
  ThemeColors
} from '@contrast-security-inc/design-system-foundations'
import { WithTooltip } from '../../design-system'
import { FormattedMessage, useIntl } from 'react-intl'
import { GradeValue } from '../../graphql/schema/types/grade-value'
import { Placement } from '@popperjs/core'
import { Link } from '@contrast-security-inc/design-system-typography'

export const getBgColorByGrade: (grade: GradeValue) => ThemeColors = (
  grade
) => {
  switch (grade) {
    case 'A':
      return 'green50'
    case 'B':
      return 'turquoise70'
    case 'C':
      return 'yellow50'
    case 'D':
      return 'orange50'
    case 'F':
      return 'red50'
    default:
      return 'neutral40'
  }
}

const gradeCss = {
  A: css`
    background-color: ${theme.color[getBgColorByGrade('A')]};
    color: ${theme.color.white};
  `,
  B: css`
    background-color: ${theme.color[getBgColorByGrade('B')]};
    color: ${theme.color.white};
  `,
  C: css`
    background-color: ${theme.color[getBgColorByGrade('C')]};
    color: ${theme.color.white};
  `,
  D: css`
    background-color: ${theme.color[getBgColorByGrade('D')]};
    color: ${theme.color.white};
  `,
  F: css`
    background-color: ${theme.color[getBgColorByGrade('F')]};
    color: ${theme.color.white};
  `,
  UNKNOWN: css`
    background-color: ${theme.color[getBgColorByGrade('UNKNOWN')]};
    color: ${theme.color.neutral80};
  `
}

const gradeBadgeCss = css`
  font-weight: ${deprecatedTheme.fontWeights.secondary.bold};
  text-align: center;
  vertical-align: middle;
  font-size: 14px;
  min-height: 24px;
  min-width: 24px;
  line-height: 23px;
  border-radius: 2px;
`

const largeGradeBadgeCss = css`
  font-size: 75px;
  height: 80px;
  width: 80px;
  line-height: 77px;
  border-radius: 4px;
`

const normalGradeBadgeCssCache = new Map<string, string>()
const largeGradeBadgeCssCache = new Map<string, string>()

Object.keys(gradeCss).forEach((gradeKey) => {
  normalGradeBadgeCssCache.set(gradeKey, cx(gradeBadgeCss, gradeCss[gradeKey]))
  largeGradeBadgeCssCache.set(
    gradeKey,
    cx(gradeBadgeCss, largeGradeBadgeCss, gradeCss[gradeKey])
  )
})

interface GradeBadgeProps {
  /**
   * The letter grade representation of the score. Contrast Server
   * provides both the value and score, which is why this isn't derived.
   */
  readonly value: GradeValue

  /**
   * If true the larger version of the badge is rendered
   */
  readonly large?: boolean

  dataTestId?: string
  readonly e2eTestId?: string
}

/**
 * Badge that displays the grade that Contrast has calculated for an
 * application or library
 */
export function GradeBadge({
  large,
  value,
  dataTestId,
  e2eTestId
}: GradeBadgeProps) {
  return (
    <div
      data-testid={dataTestId}
      data-e2e={e2eTestId ? e2eTestId : 'grade-badge-score'}
      className={
        large
          ? largeGradeBadgeCssCache.get(value)
          : normalGradeBadgeCssCache.get(value)
      }
      children={value === 'UNKNOWN' ? '?' : value}
    />
  )
}

interface AssessGradeBadgeProps extends GradeBadgeProps {
  /**
   * If an app does support assess
   */
  readonly isAssessSupported?: boolean
  /**
   * Override the tooltip's placement
   */
  readonly tooltipPlacement?: Placement
}

/**
 * Wraps GradeBadge and will display the UNKNOWN grade if assess is not supported
 */
export function AssessGradeBadge({
  large,
  value,
  isAssessSupported,
  dataTestId
}: AssessGradeBadgeProps) {
  return (
    <GradeBadge
      dataTestId={dataTestId}
      large={large}
      value={isAssessSupported ? value : 'UNKNOWN'}
    />
  )
}

const tooltipContainerCss = css`
  max-width: 200px;
`

export function AssessGradeBadgeWithTooltip(props: AssessGradeBadgeProps) {
  const intl = useIntl()
  return (
    <WithTooltip
      placement={props.tooltipPlacement || 'right'}
      renderTooltipContent={() => {
        return (
          <div className={tooltipContainerCss}>
            <p>
              {props.isAssessSupported ? (
                <FormattedMessage
                  id="detail_applicationGrade_letterGradeDescription"
                  defaultMessage="This letter grade represents an aggregate of both the number and seriousness of the vulnerabilities discovered based on the amount of the application that has been exercised."
                />
              ) : (
                <FormattedMessage id="detail_applicationGrade_letterGradeDescription_assessUnsupported" />
              )}{' '}
            </p>
            <p>
              {props.isAssessSupported && (
                <FormattedMessage
                  id="action_learnMore"
                  defaultMessage="Learn more"
                >
                  {(text) => (
                    <Link
                      href={intl.formatMessage({
                        id: 'link_applicationScoringGuide'
                      })}
                      isExternal
                      className={css`
                          color: ${theme.color.turquoise50}; !important;
                          display: flex;
                        `}
                    >
                      {text}
                    </Link>
                  )}
                </FormattedMessage>
              )}
            </p>
          </div>
        )
      }}
    >
      {() => {
        return <AssessGradeBadge {...props} />
      }}
    </WithTooltip>
  )
}
